/**
 * v0.6.10 — Filing-period enumeration for the Step-3 period picker.
 *
 * The old picker asked "pick two dates". But a VAT period is never a
 * free choice: it is the statutory period for a return the client
 * actually owes. This module turns the client's own registration data
 * into that list, so the operator selects a RETURN rather than
 * constructing a date range and hoping it lines up.
 *
 * Pure date/shape logic — no React, no network. Tested from
 * frontend/tests/filingPeriods.test.mjs.
 *
 * Cadence comes from the same places `lib/clientFrequency.js` reads
 * for its mismatch warning, deliberately:
 *   - OSS Union / Non-Union  — always QUARTERLY (Art 369f / 369)
 *   - IOSS                   — always MONTHLY  (Art 369s)
 *   - GB local               — `uk_vat_frequency`
 *   - other LOCAL countries  — `local_return_frequency[cc]`
 * If those two modules ever disagree, the mismatch warning would fire
 * on a period this list itself generated, which is the bug to watch
 * for.
 *
 * KNOWN LIMITATION, deliberate and surfaced in the UI rather than
 * hidden: a client's declared frequency is a SINGLE current value,
 * not a dated history the way registrations are. A client who moved
 * DE from monthly to quarterly in 2024 has only today's answer on
 * record, so periods generated for dates before that change assume
 * today's cadence and may be wrong. Backdated periods older than the
 * registration data can support are therefore marked `assumedCadence`
 * so the UI can say so instead of asserting it.
 */

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// v0.6.12 — SEMI_ANNUALLY added for Denmark's halvårlig cadence (ML § 57,
// stk. 4), after an operator's own Danish return arrived for 01/07-31/12.
// Ordered shortest period first, as the dropdown reads.
export const CADENCES = ["MONTHLY", "QUARTERLY", "SEMI_ANNUALLY", "ANNUALLY"];

// Hard ceiling on how many periods we will enumerate, whatever the
// registration date says. 10 years of monthly returns is already far
// past anything filable; without a bound, one bad `effective_from`
// (a typo'd "1900-01-01", say) would render tens of thousands of rows.
export const MAX_PERIODS = 120;

const iso = (y, m /* 1-indexed */, d) =>
  `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const lastDayOfMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/**
 * Normalise a stored registration date to ISO, or null if it can't be
 * understood.
 *
 * v0.6.10 fix — `registration_date` is NOT reliably ISO in live data.
 * The client edit form's "Effective from" placeholder says YYYY-MM-DD
 * but accepts anything, and the production Default Client holds a mix:
 * "2020/01/01" on some rows, "01/01/2022" (DD/MM/YYYY) on others.
 *
 * That difference silently broke the picker. Enumeration derives its
 * start year from `fromIso.slice(0, 4)`; on "01/01/2022" that is
 * Number("01/0") === NaN, every `y >= NaN` comparison is false, the
 * loop never runs, and the cadence yielded ZERO periods. Quarterly
 * survived only because "2020/01/01" happens to begin with its year.
 *
 * Accepted: YYYY-MM-DD, YYYY/MM/DD, DD/MM/YYYY, DD-MM-YYYY.
 * Day-first is the app's pinned convention (see DateInput's
 * DD/MM/YYYY guarantee, v0.4.37 Q2), so an ambiguous "03/04/2022" is
 * read as 3 April — consistent with what the operator typed it into.
 */
export function toIsoDate(value) {
  if (!value) return null;
  const s = String(value).trim().slice(0, 10);
  let m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(s);
  if (!m) {
    const dmy = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(s);
    if (dmy) m = [s, dmy[3], dmy[2], dmy[1]];
  }
  if (!m) return null;
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  if (d > lastDayOfMonth(y, mo)) return null;
  return iso(y, mo, d);
}


/** ISO date string for "today", injectable for tests. */
const todayIso = (today = new Date()) =>
  iso(today.getUTCFullYear(), today.getUTCMonth() + 1, today.getUTCDate());

const FIXED_SCHEME_CADENCE = {
  IOSS: "MONTHLY",
  OSS_UNION: "QUARTERLY",
  OSS_NON_UNION: "QUARTERLY",
  OSS: "QUARTERLY",
};

// v0.6.10 — scheme labels matching lib/clientFrequency.js's SCHEME_LABEL,
// so the same registration is named identically wherever it appears.
// Previously built inline as `${cc} ${rt.replace("_", " ")}`, which
// rendered OSS_NON_UNION as the shouty "IE OSS NON UNION".
const SCHEME_LABEL = {
  IOSS: "IOSS",
  OSS_UNION: "OSS Union",
  OSS_NON_UNION: "OSS Non-Union",
  OSS: "OSS",
};

const norm = (s) => (s || "").toUpperCase();

/**
 * Every distinct cadence this client files on, with the registrations
 * that drive each one. Sorted MONTHLY → QUARTERLY → SEMI_ANNUALLY →
 * ANNUALLY.
 *
 * Returns `[{ cadence, labels: [...], earliest: iso|null }]`.
 * `earliest` is the oldest `registration_date` among that cadence's
 * registrations, or null when none of them record one.
 */
export function cadencesFor(client) {
  const regs = client?.company_vat_numbers || [];
  const localFreq = client?.local_return_frequency || {};
  const byCadence = new Map();

  const add = (cadence, label, date, rawDate) => {
    if (!CADENCES.includes(cadence)) return;
    if (!byCadence.has(cadence)) {
      byCadence.set(cadence, {
        cadence, labels: [], earliest: null, unreadableDates: [],
      });
    }
    const e = byCadence.get(cadence);
    if (!e.labels.includes(label)) e.labels.push(label);
    if (date && (!e.earliest || date < e.earliest)) e.earliest = date;
    // A date that EXISTS but could not be parsed is a different
    // problem from no date at all, and the UI must say which — the
    // first is bad stored data to go and fix, the second is simply
    // unrecorded. Conflating them produced a message claiming the
    // client had no registrations while seven were listed on screen.
    if (!date && rawDate && !e.unreadableDates.includes(label)) {
      e.unreadableDates.push(label);
    }
  };

  for (const r of regs) {
    const cc = norm(r.country || r.country_code);
    const rt = norm(r.registration_type) || "LOCAL";
    // Normalised, never the raw stored string — live data mixes
    // "2020/01/01" and "01/01/2022" (see toIsoDate).
    const date = toIsoDate(r.registration_date);
    const fixed = FIXED_SCHEME_CADENCE[rt];
    if (fixed) {
      // v0.6.10 — IOSS used to be labelled bare "IOSS", dropping its
      // country. Next to a local registration on the same cadence the
      // two labels ran together as "DE IOSS", which reads as a German
      // IOSS registration rather than what it is: a German LOCAL
      // registration and a separate IOSS scheme. Carry the country on
      // every scheme label, exactly as the frequency warning does.
      add(fixed, `${cc ? `${cc} ` : ""}${SCHEME_LABEL[rt]}`,
          date, r.registration_date);
      continue;
    }
    if (rt !== "LOCAL") continue;
    if (!cc) continue;
    const declared = cc === "GB"
      ? norm(client?.uk_vat_frequency) || "QUARTERLY"
      : norm(localFreq[cc]) || "QUARTERLY";
    add(declared, cc, date, r.registration_date);
  }

  for (const e of byCadence.values()) {
    e.labels.sort();
    e.unreadableDates.sort();
  }
  return CADENCES.filter((c) => byCadence.has(c)).map((c) => byCadence.get(c));
}

/**
 * Enumerate every complete statutory period of `cadence` that ENDS on
 * or after `fromIso` and on or before `toIso`, newest first.
 *
 * Only COMPLETE periods are returned: a period whose end is still in
 * the future is not yet filable, and offering it invites a calc run
 * against partial data. (The existing quick-period chips still offer
 * "This month"/"This quarter" for interim previews — that stays the
 * escape hatch, and it is labelled as the current period there.)
 */
export function periodsForCadence(cadence, fromIso, toIso, yearEndMonth = 12, quarterOffset = 0) {
  if (!CADENCES.includes(cadence) || !fromIso || !toIso) return [];
  if (fromIso > toIso) return [];
  const [ty, tm] = toIso.split("-").map(Number);
  if (!Number.isFinite(ty) || !Number.isFinite(tm)) return [];

  const out = [];
  const push = (y, startM, endM, label) => {
    const start = iso(y, startM, 1);
    const end = iso(y, endM, lastDayOfMonth(y, endM));
    if (end > toIso || end < fromIso) return;
    out.push({ key: `${cadence}:${start}`, cadence, label, start, end });
  };
  // v0.6.12 — a span that may cross a calendar year, for an annual period
  // whose year end is not December. `push` above builds both ends in the
  // same year, which is exactly the assumption that made every annual filer
  // a January-December one.
  const pushSpan = (startY, startM, endY, endM, label) => {
    const start = iso(startY, startM, 1);
    const end = iso(endY, endM, lastDayOfMonth(endY, endM));
    if (end > toIso || end < fromIso) return;
    out.push({ key: `${cadence}:${start}`, cadence, label, start, end });
  };

  // v0.6.10 fix — an unparseable `fromIso` used to make this NaN, and
  // `y >= NaN` is false for every y, so the loop silently produced an
  // EMPTY list rather than failing loudly. Refuse the input instead:
  // callers fall back to a usable window (see buildPeriodOptions).
  const startYear = Number(fromIso.slice(0, 4));
  if (!Number.isFinite(startYear)) return [];
  const yemRaw = Number(yearEndMonth);
  const yem = Number.isFinite(yemRaw) && yemRaw >= 1 && yemRaw <= 12
    ? Math.trunc(yemRaw) : 12;
  for (let y = ty; y >= startYear && out.length < MAX_PERIODS; y -= 1) {
    if (cadence === "MONTHLY") {
      for (let m = 12; m >= 1 && out.length < MAX_PERIODS; m -= 1) {
        push(y, m, m, `${MONTH_ABBR[m - 1]} ${y}`);
      }
    } else if (cadence === "QUARTERLY" && quarterOffset) {
      // Roadmap 5b.4 (A2-390) — a UK stagger group's quarters (HMRC VAT
      // Notice 700 s20.3.1): group 2 ends Jan/Apr/Jul/Oct, group 3
      // Feb/May/Aug/Nov. A quarter ending in Jan or Feb starts the year before.
      for (let q = 4; q >= 1 && out.length < MAX_PERIODS; q -= 1) {
        const endM = q * 3 - 3 + quarterOffset || 12;
        const endY = endM === 12 && q * 3 - 3 + quarterOffset === 0 ? y - 1 : y;
        const startIdx = endY * 12 + (endM - 1) - 2;
        const startY = Math.floor(startIdx / 12);
        const startM = (startIdx % 12) + 1;
        pushSpan(startY, startM, endY, endM,
          `${MONTH_ABBR[startM - 1]}–${MONTH_ABBR[endM - 1]} ${endY}`);
      }
    } else if (cadence === "QUARTERLY") {
      for (let q = 4; q >= 1 && out.length < MAX_PERIODS; q -= 1) {
        push(y, q * 3 - 2, q * 3, `Q${q} ${y}`);
      }
    } else if (cadence === "SEMI_ANNUALLY") {
      // v0.6.12 — without this branch a half-yearly client fell into the
      // annual `else` below and was offered one 12-month period, which is
      // not a period they file.
      for (let h = 2; h >= 1 && out.length < MAX_PERIODS; h -= 1) {
        push(y, h * 6 - 5, h * 6, `H${h} ${y}`);
      }
    } else if (yem === 12) {
      push(y, 1, 12, String(y));
    } else {
      // HMRC VATAAS3300: the business chooses the month its annual
      // accounting year ends in. A March year end runs 1 Apr to 31 Mar, so
      // the period crosses a calendar year and is named by the date it ends
      // — "to 31 Mar 2026" — because "2026" alone would read as a calendar
      // year the client does not file.
      pushSpan(y - 1, yem + 1, y, yem,
        `to ${String(lastDayOfMonth(y, yem)).padStart(2, "0")} ${MONTH_ABBR[yem - 1]} ${y}`);
    }
  }
  return out;
}

/**
 * The full picker model for one client + cadence.
 *
 * `earliestData` (the earliest transaction date across the selected
 * ingest runs, when known) widens the window backwards for a client
 * whose registrations carry no `registration_date` — the GB row in
 * the live data has an empty "Effective from", so registration alone
 * cannot bound the list.
 *
 * Each period is tagged:
 *   hasData        — the selected runs cover any part of this period
 *   fullyCovered   — the selected runs span the whole period
 *   beforeRegistration — ends before the cadence's earliest known
 *                    registration date (shown, not hidden: late
 *                    registration and retrospective effective dates
 *                    are exactly when an operator needs these)
 */
export function buildPeriodOptions({
  client, cadence, dataRange = null, today = new Date(),
}) {
  const info = cadencesFor(client).find((c) => c.cadence === cadence)
    || { cadence, labels: [], earliest: null };
  const to = todayIso(today);
  // Both candidates are normalised: `info.earliest` by cadencesFor,
  // and dataRange (transaction dates) defensively, since anything
  // non-ISO reaching periodsForCadence yields an empty list.
  const candidates = [info.earliest, toIsoDate(dataRange?.start)]
    .filter(Boolean);
  const fallbackFrom = `${Number(to.slice(0, 4)) - 2}-01-01`;
  // No usable date anywhere → a 2-year window, rather than an empty
  // list the operator cannot act on.
  const from = candidates.length ? candidates.sort()[0] : fallbackFrom;

  // Belt and braces: if enumeration still comes back empty for a
  // cadence the client genuinely files on, fall back to the window
  // rather than handing the UI nothing. An empty list here is always
  // a bug in our own date handling, never a real answer.
  // v0.6.12 — the client's own UK annual-accounting year end, when they
  // have one. Absent means December, i.e. unchanged behaviour.
  const yearEndMonth = client?.uk_annual_year_end_month || 12;
  const qOffset = cadence === "QUARTERLY" ? ukQuarterOffset(client) : 0;
  let periods = periodsForCadence(cadence, from, to, yearEndMonth, qOffset);
  if (!periods.length && from !== fallbackFrom) {
    periods = periodsForCadence(cadence, fallbackFrom, to, yearEndMonth, qOffset);
  }

  // Normalised, not raw. These are compared as STRINGS against
  // date-only period bounds, so a datetime ("2025-01-01T00:00:00")
  // would sort AFTER the date-only string for the same day and
  // silently downgrade a fully-covered period to "partial". The
  // backend now emits date-only, but a caller passing anything else
  // must not corrupt the badges.
  return periods.map((p) => {
    const covStart = toIsoDate(dataRange?.start);
    const covEnd = toIsoDate(dataRange?.end);
    const hasData = !!covStart && !!covEnd
      && covStart <= p.end && covEnd >= p.start;
    return {
      ...p,
      hasData,
      fullyCovered: hasData && covStart <= p.start && covEnd >= p.end,
      beforeRegistration: !!info.earliest && p.end < info.earliest,
      assumedCadence: !info.earliest,
    };
  });
}

export function periodYear(p) {
  return p.start.slice(0, 4);
}

/**
 * Which statutory cadence this exact range IS, or null when it is not
 * a whole statutory period at all.
 *
 * Stricter than a day-count heuristic on purpose: a 90-day range that
 * starts on the 14th is not a quarter, however close its length is to
 * one. Alignment to calendar boundaries is what makes a range a return
 * period, and that is precisely what decides whether the operator has
 * picked a real filing period or an arbitrary window.
 */
/**
 * Roadmap 5b.4 (A2-390) — months a UK client's quarters are shifted by its
 * HMRC stagger group (Notice 700 s20.3.1): 0 for group 1 (calendar quarters,
 * and for a client with no group recorded), 1 for group 2, 2 for group 3.
 * Only when the client's QUARTERLY return is the UK one alone: another
 * country's quarters stay calendar quarters.
 */
export function ukQuarterOffset(client) {
  const g = Number(client?.uk_stagger_group);
  if (g !== 2 && g !== 3) return 0;
  const q = cadencesFor(client).find((c) => c.cadence === "QUARTERLY");
  return q && q.labels.length === 1 && q.labels[0] === "GB" ? g - 1 : 0;
}

export function classifyStatutoryPeriod(startIso, endIso) {
  const s = toIsoDate(startIso);
  const e = toIsoDate(endIso);
  if (!s || !e || s > e) return null;
  const [sy, sm, sd] = s.split("-").map(Number);
  const [ey, em, ed] = e.split("-").map(Number);
  if (sd !== 1) return null;                       // must start a month
  if (ed !== lastDayOfMonth(ey, em)) return null;  // must end one
  if (sy === ey && sm === em) return "MONTHLY";
  if (sy === ey && sm === 1 && em === 12) return "ANNUALLY";
  if (sy === ey && em - sm === 5 && (sm === 1 || sm === 7)) return "SEMI_ANNUALLY";
  if (sy === ey && em - sm === 2 && (sm - 1) % 3 === 0) return "QUARTERLY";
  return null;
}

/**
 * Operator check 2026-10-16: the calculation step set the filing period to
 * the uploads' raw date span, so a file with one sale on 10 September gave
 * a "September" return covering 10–10 September, and it was filed. A return
 * covers the client's whole tax period. This is the cadence to round to:
 * the client's NATIONAL return frequency when its local registrations all
 * share one, else its only cadence, else null (the operator chooses).
 */
export function defaultCadence(client) {
  const local = Object.values(client?.local_return_frequency || {})
    .map((c) => (c || "").toUpperCase()).filter((c) => CADENCES.includes(c));
  if (local.length && local.every((c) => c === local[0])) return local[0];
  const all = cadencesFor(client).map((c) => c.cadence);
  return all.length === 1 ? all[0] : null;
}

/**
 * The whole statutory period of `cadence` containing both dates, or null
 * when they fall in different periods (or the input is unusable).
 */
export function snapToPeriod(minIso, maxIso, cadence, quarterOffset = 0) {
  const a = toIsoDate(minIso);
  const b = toIsoDate(maxIso);
  if (!a || !b || a > b) return null;
  const months = { MONTHLY: 1, QUARTERLY: 3, SEMI_ANNUALLY: 6, ANNUALLY: 12 }[cadence];
  if (!months) return null;
  const [ay, am] = a.split("-").map(Number);
  const [by, bm] = b.split("-").map(Number);
  if (cadence === "QUARTERLY" && quarterOffset) {
    // Roadmap 5b.4 — a stagger-group quarter may cross a year end.
    const qStart = (y, m) => Math.floor((y * 12 + m - 1 - quarterOffset) / 3) * 3 + quarterOffset;
    if (qStart(ay, am) !== qStart(by, bm)) return null;
    const s0 = qStart(ay, am);
    const [sy, smo] = [Math.floor(s0 / 12), (s0 % 12) + 1];
    const [ey, emo] = [Math.floor((s0 + 2) / 12), ((s0 + 2) % 12) + 1];
    const pad = (n) => String(n).padStart(2, "0");
    return { start: `${sy}-${pad(smo)}-01`, end: `${ey}-${pad(emo)}-${pad(lastDayOfMonth(ey, emo))}` };
  }
  const startMonth = (m) => Math.floor((m - 1) / months) * months + 1;
  if (ay !== by || startMonth(am) !== startMonth(bm)) return null;
  const sm = startMonth(am);
  const em = sm + months - 1;
  const pad = (n) => String(n).padStart(2, "0");
  return { start: `${ay}-${pad(sm)}-01`, end: `${ay}-${pad(em)}-${pad(lastDayOfMonth(ay, em))}` };
}

/**
 * v0.4.37 CL3A — the filing period auto-detected from the selected ingest
 * runs: min/max of their date ranges, rounded to the client's whole tax
 * period when all dates fall inside one (operator check 2026-10-16).
 * The operator can still edit it freely afterwards.
 */
export function detectFilingRange(selectedRuns, client) {
  let min = null, max = null;
  for (const r of selectedRuns || []) {
    const dr = r.date_range || {};
    const mn = dr.min || dr.first_tx_date;
    const mx = dr.max || dr.last_tx_date;
    if (mn && (!min || mn < min)) min = mn;
    if (mx && (!max || mx > max)) max = mx;
  }
  if (!(min && max)) return null;
  // A2-392 (operator check 4, 2026-10-16): an upload of nothing but sales to
  // consumers in other EU countries is a Union OSS return, which is one per
  // calendar quarter (Dir 2006/112 Art 369f), whatever the client's national
  // cadence. It defaulted to the national month (May, not April-June).
  const ossClient = (client?.company_vat_numbers || []).some((r) =>
    ["OSS_UNION", "OSS"].includes((r.registration_type || "").toUpperCase()));
  const ossOnly = ossClient && (selectedRuns || []).length > 0
    && selectedRuns.every((r) => r.oss_sales_only === true);
  const cadence = ossOnly ? "QUARTERLY" : defaultCadence(client);
  // Operator check 2026-10-16 (second pass): dates spanning two periods left
  // the raw span (25 Sep - 5 Oct), which is never a tax period. Default to
  // the LATEST whole period — the one normally being filed — and let the
  // filed-period note name the earlier rows. The operator can still edit it.
  const qOffset = cadence === "QUARTERLY" ? ukQuarterOffset(client) : 0;
  return snapToPeriod(min, max, cadence, qOffset) || snapToPeriod(max, max, cadence, qOffset)
    || { start: min, end: max };
}
